import type { BalanceType, UserInfoType } from '../../../api/fetchers/userInfo/types';
import { useGameConfig } from '../../../config/GameConfigProvider';
import { type CoreRootState, useCoreSelector } from '../../CoreStore';

import * as rawSelectors from './rawSelectors';
import type {
  CurrencyInfo,
  DealerType,
  TableCloseNotifyType,
  TableType,
  WinnersTotal,
  WinnerType,
  WinResultType,
} from './types';
import { GameStatus } from './types';

export const useWinners = (): WinnerType[] => {
  const winners = useCoreSelector(({ gameState }: CoreRootState) => gameState.winners);
  if (!Array.isArray(winners)) {
    // FIXME: if there is only one winner, the value received is an object, instead of an array. Adjust on server or on fetcher
    return winners ? [(winners as any).winner] : [];
  }
  return winners;
};

export const useIsBettingAllowedConnected = (): boolean => {
  const customSelector = useGameConfig().config.bettingAllowedSelector;
  return useCoreSelector(customSelector ?? rawSelectors.selectIsBetsOpen);
};

export const deductibleBalanceSelector = (state: CoreRootState): number => {
  return (state.gameState.userInfo?.balance.amount || 0) + (state.gameState.userInfo?.balance.bonus || 0);
};

export const useGameStateSlice = {
  hideUI: (): boolean => {
    return useCoreSelector(rawSelectors.selectHideUI);
  },
  winners: (): WinnerType[] => {
    return useCoreSelector(rawSelectors.selectWinners);
  },
  getWinnersTotal: (): WinnersTotal | null => {
    return useCoreSelector(rawSelectors.selectWinnersTotal);
  },
  winResult: (): WinResultType | null => {
    return useCoreSelector(rawSelectors.selectWinResult);
  },
  getCurrentStatus: (): GameStatus | string => {
    return useCoreSelector(rawSelectors.selectCurrentStatus);
  },
  getTimer: (): number => {
    return useCoreSelector(rawSelectors.selectTimer);
  },
  getTimerStartTime: (): number => {
    return useCoreSelector(rawSelectors.selectTimerStartTime);
  },
  isBetsOpenStatus: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsBetsOpen);
  },
  gameId: (): string | null => {
    return useCoreSelector(rawSelectors.selectGameId);
  },
  gameChannel: (): string | null => {
    return useCoreSelector(rawSelectors.selectGameChannel);
  },
  gameName: (): string | null => {
    return useCoreSelector(rawSelectors.selectGameName);
  },
  getGameSubscribed: (): boolean => {
    return useCoreSelector(rawSelectors.selectGameSubscribed);
  },
  userInfoReady: (): boolean => {
    return useCoreSelector(rawSelectors.selectUserInfoReady);
  },
  userInfo: (): UserInfoType | null => {
    return useCoreSelector(rawSelectors.selectUserInfo);
  },
  userName: (): string | undefined => {
    return useCoreSelector(rawSelectors.selectUserScreenName);
  },
  balance: (): BalanceType | undefined => {
    return useCoreSelector(rawSelectors.selectUserBalance);
  },
  getDeductibleBalance: (): number => {
    return useCoreSelector(rawSelectors.selectDeductibleBalance);
  },
  getCurrency: (): string | null => {
    return useCoreSelector(rawSelectors.selectCurrency);
  },
  getNetPosition: (): number => {
    return useCoreSelector(rawSelectors.selectNetPosition);
  },
  getCurrencyInfo: (): CurrencyInfo | null => {
    return useCoreSelector(rawSelectors.selectCurrencyInfo);
  },
  getCurrencyDecimals: (): number => {
    return useCoreSelector(rawSelectors.selectCurrencyDecimals);
  },
  getOpenedLeftDrawer: (): string | null => {
    return useCoreSelector(rawSelectors.selectLeftDrawerOpened);
  },
  dealer: (): DealerType | null => {
    return useCoreSelector(rawSelectors.selectDealer);
  },
  table: (): TableType | null => {
    return useCoreSelector(rawSelectors.selectGameTable);
  },
  getIsGamePaused: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsGamePaused);
  },
  getIsGameAbandoned: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsGameAbandoned);
  },
  getIsGameSuspended: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsGameSuspended);
  },
  isLoadedInActiveRound: (): boolean => {
    return useCoreSelector(rawSelectors.selectIsLoadedInActiveRound);
  },
  getVideoPoster: (): string => {
    return useCoreSelector(rawSelectors.selectVideoPoster);
  },
  getTableCloseNotification: (): TableCloseNotifyType | null => {
    return useCoreSelector(rawSelectors.selectTableCloseNotify);
  },
  getWaitForWinResult: (): boolean => {
    return useCoreSelector(({ gameState }) => gameState.waitForWinResult);
  },
};
